
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsContent } from '@/components/ui/tabs'

import { UnderlinedTabsList } from '@/components/atoms'

import { ProductModel } from '@/types/model/Product'

import {
  ProductDetailsTab,
  ProductOverviewTab,
  ProductVariationsTab,
  ProductViewHeader,
} from './view-dialog'
import { Spinner } from '@/components/ui/spinner'

interface ProductViewDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  product: ProductModel.Product | null
  isLoading?: boolean
  onEdit: (product: ProductModel.Product) => void
  onDelete: (product: ProductModel.Product) => void
}

export const ProductViewDialog = ({
  open,
  onOpenChange,
  product,
  isLoading,
  onEdit,
  onDelete,
}: ProductViewDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="flex flex-col gap-0 p-0">
        {!product || isLoading ? (
          <div className="flex min-h-[400px] flex-col items-center justify-center gap-3">
            <Spinner className="size-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading product details…</p>
          </div>
        ) : (
          <>
            <ProductViewHeader product={product} onEdit={onEdit} onDelete={onDelete} />

            <Tabs defaultValue="overview" className="flex min-h-0 flex-1 flex-col">
              <div className="px-5">
                <UnderlinedTabsList
                  tabs={[
                    { label: 'Overview', value: 'overview' },
                    {
                      label: `Variations (${product.variations?.length || 0})`,
                      value: 'variations',
                    },
                    { label: 'Details', value: 'details' },
                  ]}
                  className="gap-5"
                  triggerClassName="px-0"
                />
              </div>

              <ScrollArea className="flex-1">
                <div className="p-5">
                  <TabsContent value="overview" className="mt-0">
                    <ProductOverviewTab
                      product={product}
                      minPrice={Math.min(
                        ...(product.variations?.map((v) => v.regular_price) || [0])
                      )}
                      maxPrice={Math.max(
                        ...(product.variations?.map((v) => v.regular_price) || [0])
                      )}
                      totalStock={
                        product.variations?.reduce(
                          (sum, v) => sum + (v.initial_stock_quantity || 0),
                          0
                        ) || 0
                      }
                    />
                  </TabsContent>

                  <TabsContent value="variations" className="mt-0">
                    <ProductVariationsTab product={product} />
                  </TabsContent>

                  <TabsContent value="details" className="mt-0">
                    <ProductDetailsTab product={product} />
                  </TabsContent>
                </div>
              </ScrollArea>
            </Tabs>
          </>
        )}

        <DialogFooter className="px-5 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
